// =========================================================================
// AlertCard — per-card projections. Fork-owned (zone C).
//
// Transformation layer: pure functions, no React, no fetches, no mutation.
//
// @see src/versions/v1/modules/issues/AlertCard.tsx
// @see docs/logic/issues/alerts.md
// =========================================================================

import type { SourceCalc } from '@/versions/v1/chrome-oem/SourceDrawerBody';
import { fmt$, groupNum, type Coi } from '@/versions/v1/shared/oem-engine';
import { CALC_NOTE } from '@/versions/v1/shared/source-calc';

/**
 * rateCardCalc(coi) → SourceCalc
 * Projection rule: the demo's `FIG('A$…/h', 'efac', {}, {calc})` on the "If
 *   it fails first" cell (line 3138). The formula is the demo's note verbatim;
 *   the steps rebuild `coi()`'s unplanned figure from its parts. The parts the
 *   failure takes with it are not on `Coi`, so they are the remainder — the
 *   same number `COI[id].partsU` holds.
 */
export function rateCardCalc(coi: Coi): SourceCalc {
  const lostProduction = coi.hU * coi.lost;
  const extraLabour = coi.lab * 1.6 * coi.rate;
  const parts = coi.unplanned - lostProduction - extraLabour;
  return {
    formula: CALC_NOTE.rateCard,
    steps: [
      `${coi.hU} h × A$${groupNum(coi.lost)}/h = ${fmt$(lostProduction)} lost production`,
      `+ ${fmt$(parts)} parts the failure takes with it`,
      `+ ${coi.lab} h × 1.6 × A$${groupNum(coi.rate)}/h = ${fmt$(extraLabour)} labour`,
      `= ${fmt$(coi.unplanned)} if it fails first`,
    ],
  };
}
